import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, ListItem, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { isVariantJobRunning, variantService, type CourseFamily, type VariantJob, type VariantKind, type VariantLanguage } from '../../../services/variantService';

const errorMessage = (e: unknown, fallback: string): string => {
    const data = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
    return data?.message ?? data?.error ?? fallback;
};

/**
 * Tranche E2 (#489): variants of a v2 course — family list (outdated badge,
 * quality warning per language), running jobs with progress, "create" form.
 */
export const CourseVariantsDialog: React.FC<{ courseId: number; open: boolean; onClose: () => void }> = ({ courseId, open, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const [family, setFamily] = useState<CourseFamily | null>(null);
    const [jobs, setJobs] = useState<VariantJob[]>([]);
    const [languages, setLanguages] = useState<VariantLanguage[]>([]);
    const [kind, setKind] = useState<VariantKind>('translation');
    const [language, setLanguage] = useState('en');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const [fam, jobList] = await Promise.all([variantService.family(courseId), variantService.jobs(courseId)]);
            setFamily(fam);
            setJobs(jobList);
            setError(null);
        } catch (e) {
            setError(errorMessage(e, translate('variants.loadError')));
        }
    }, [courseId, translate]);

    useEffect(() => {
        if (!open) return;
        void load();
        variantService.languages().then(setLanguages).catch(() => setLanguages([]));
    }, [open, load]);

    useEffect(() => {
        if (!open || !jobs.some((j) => isVariantJobRunning(j.status))) return undefined;
        const timer = window.setInterval(() => { void load(); }, 4000);
        return () => window.clearInterval(timer);
    }, [open, jobs, load]);

    const original = family?.variants.find((m) => m.isOriginal) ?? null;
    const isOriginalCourse = family ? family.originalId === courseId : true;
    const selected = languages.find((l) => l.code === language);

    const create = async () => {
        setSubmitting(true);
        try {
            await variantService.create({ sourceCourseId: family?.originalId ?? courseId, kind, targetLanguage: kind === 'translation' ? language : undefined });
            notify(translate('variants.started'), { type: 'success' });
            await load();
        } catch (e) {
            notify(errorMessage(e, translate('variants.createError')), { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TranslateOutlined /> {translate('variants.title')}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!family && !error && <CircularProgress size={24} />}
                {family && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{translate('variants.family')}</Typography>
                        <List dense disablePadding data-testid="variant-family">
                            {family.variants.map((m) => (
                                <ListItem key={m.id} disableGutters secondaryAction={!m.isCurrent && <Button size="small" onClick={() => { onClose(); navigate(`/courses/${m.id}/builder`); }}>{translate('variants.open')}</Button>}>
                                    <ListItemText
                                        primary={<>
                                            <Chip size="small" label={m.languageLabel} sx={{ mr: 1 }} color={m.isOriginal ? 'primary' : 'default'} />
                                            {m.title}
                                            {m.isOriginal && <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={translate('variants.original')} />}
                                            {!m.isActive && <Chip size="small" variant="outlined" color="warning" sx={{ ml: 1 }} label={translate('variants.inactive')} />}
                                            {m.outdated && <Chip size="small" color="warning" sx={{ ml: 1 }} label={translate('variants.outdated')} data-testid={`variant-outdated-${m.id}`} />}
                                            {m.qualityTier === 'review' && <Chip size="small" color="error" variant="outlined" sx={{ ml: 1 }} label={translate('variants.reviewRequired')} />}
                                        </>}
                                    />
                                </ListItem>
                            ))}
                        </List>

                        {jobs.length > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>{translate('variants.jobs')}</Typography>
                                {jobs.map((job) => (
                                    <div key={job.id} data-testid={`variant-job-${job.id}`} style={{ marginBottom: 8 }}>
                                        <Typography variant="body2">
                                            {translate(`variants.kind.${job.kind}`)}{job.kind === 'translation' ? ` → ${languages.find((l) => l.code === job.targetLanguage)?.label ?? job.targetLanguage}` : ''} · {translate(`variants.status.${job.status}`)}
                                            {job.status === 'processing' && job.progressLabel ? ` · ${job.progressLabel}` : ''}
                                            {job.status === 'completed' && job.skippedLessons.length > 0 ? ` · ${translate('variants.skipped', { count: job.skippedLessons.length })}` : ''}
                                        </Typography>
                                        {isVariantJobRunning(job.status) && <LinearProgress variant={job.totalLessons > 0 ? 'determinate' : 'indeterminate'} value={job.totalLessons > 0 ? (100 * job.completedLessons) / job.totalLessons : 0} />}
                                        {job.errorMessage && <Typography variant="caption" color="error">{job.errorMessage}</Typography>}
                                    </div>
                                ))}
                            </>
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{translate('variants.create')}</Typography>
                        {!isOriginalCourse && <Alert severity="info" sx={{ mb: 1 }}>{translate('variants.fromOriginal', { title: original?.title ?? '' })}</Alert>}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <TextField select size="small" label={translate('variants.kindLabel')} value={kind} onChange={(e) => setKind(e.target.value as VariantKind)} sx={{ minWidth: 200 }} inputProps={{ 'aria-label': translate('variants.kindLabel') }}>
                                <MenuItem value="translation">{translate('variants.kind.translation')}</MenuItem>
                                <MenuItem value="easy_language">{translate('variants.kind.easy_language')}</MenuItem>
                            </TextField>
                            {kind === 'translation' && (
                                <TextField select size="small" label={translate('variants.language')} value={language} onChange={(e) => setLanguage(e.target.value)} sx={{ minWidth: 220 }} inputProps={{ 'aria-label': translate('variants.language') }}>
                                    {languages.filter((l) => l.code !== family.language).map((l) => <MenuItem key={l.code} value={l.code}>{l.label} ({l.nativeLabel}){l.tier === 'review' ? ' ⚠' : ''}</MenuItem>)}
                                </TextField>
                            )}
                            <Button variant="contained" onClick={() => void create()} disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <TranslateOutlined />}>
                                {translate('variants.submit')}
                            </Button>
                        </div>
                        {kind === 'translation' && selected?.tier === 'review' && <Alert severity="warning" sx={{ mt: 1 }}>{translate('variants.reviewHint')}</Alert>}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{translate('variants.hint')}</Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>{translate('ra.action.close')}</Button></DialogActions>
        </Dialog>
    );
};

export default CourseVariantsDialog;
